import { Injectable } from '@angular/core';
import { CanActivate, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, defer, finalize, map, Observable } from 'rxjs';
import { filter, take, tap } from 'rxjs/operators';
import { ResourceStatus, ResourceType } from '@completion/enums';
import { UserFacade } from '../facades/user.facade';
import { getCurrentProject, getCurrentUser, getProjectAccess, getProjectLoading, getResourceState } from '../store/selectors';
import { ProjectsLoad } from '../store/actions';

@Injectable({
  providedIn: 'root',
})

/** Core data guard will make sure current user and current project is selected before rest of component tree loads */
export class CoreDataGuard implements CanActivate {
  isProjectLoaded$ = defer(() => this.store.select(getCurrentProject).pipe(map((project) => !!project)));
  isUserLoaded$ = defer(() => this.store.select(getCurrentUser).pipe(map((user) => user.id !== 0)));
  isProjectAccessLoaded$ = defer(() => this.store.select(getProjectAccess).pipe(map((access) => !!access)));
  isProjectsLoading$ = defer(() => this.store.select(getProjectLoading));
  currentUserResourceState$ = defer(() => this.store.select(getResourceState, ResourceType.UserGetCurrentUser));
  projectsResourceState$ = defer(() => this.store.select(getResourceState, ResourceType.GetProjects));
  private hasRequestedUser = false;
  private hasRequestedProjects = false;

  private static isServiceUnavailableStatus(status?: number): boolean {
    return status === 0 || (typeof status === 'number' && status >= 500);
  }

  constructor(
    private store: Store,
    private userFacade: UserFacade,
    private router: Router,
  ) {}

  canActivate(): Observable<boolean> {
    return combineLatest([
      this.isProjectLoaded$,
      this.isUserLoaded$,
      this.isProjectAccessLoaded$,
      this.isProjectsLoading$,
      this.currentUserResourceState$,
      this.projectsResourceState$,
    ]).pipe(
      tap(([projectLoaded, userLoaded, _, isProjectsLoading]) => {
        if (!userLoaded && !this.hasRequestedUser) {
          this.hasRequestedUser = true;
          void this.userFacade.init();
        }

        if (!projectLoaded && !isProjectsLoading && !this.hasRequestedProjects) {
          this.hasRequestedProjects = true;
          this.store.dispatch(new ProjectsLoad());
        }
      }),
      map(([projectLoaded, userLoaded, projectAccessLoaded, _isProjectsLoading, currentUserState, projectsState]) => {
        if (currentUserState.status === ResourceStatus.Failure || projectsState.status === ResourceStatus.Failure) {
          if (
            CoreDataGuard.isServiceUnavailableStatus(currentUserState.lastErrorStatusNumber) ||
            CoreDataGuard.isServiceUnavailableStatus(projectsState.lastErrorStatusNumber)
          ) {
            void this.router.navigate(['/service-unavailable']);
            return false; // done: error page
          }
          void this.router.navigate(['/no-project-access']);
          return false; // done: error page
        }

        if (projectLoaded && userLoaded && projectAccessLoaded) {
          return true; // done: all OK
        }

        return null; // still loading => keep going
      }),
      filter((canActivate: boolean | null): canActivate is boolean => canActivate !== null),
      take(1),
      finalize(() => this.resetBootstrapFlags()),
    );
  }

  private resetBootstrapFlags(): void {
    this.hasRequestedUser = false;
    this.hasRequestedProjects = false;
  }
}
